import { FormCommonProps } from "src/components/common/forms/types.ts";
import i18n from "i18next";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { PartConfigurationAttributesApi } from "src/api/generated/erp/configurator/api/partConfigurationAttributesApi.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { useStoredState } from "src/utils/useStoredState.ts";
import { ObjectAttributeField } from "src/api/generated/erp/common/objectAttributes/model/objectAttributeField.ts";
import { requireRule } from "src/components/common/forms/validation.ts";
import { ObjectAttributeFieldFormComponent } from "src/components/views/erp/objectAttributes/ObjectAttributeFieldFormComponent.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface PartConfigurationAttributeUpdateFormProps extends FormCommonProps<void> {
	configurationIds: Array<number>;
}

interface FormValues {
	attributeFieldId: number | null;
	newValue: string | null;
}

export const PartConfigurationAttributeUpdateForm = ({
	configurationIds,
	onFormEdited,
	onCompleted,
}: PartConfigurationAttributeUpdateFormProps) => {
	const [storedFieldId, setStoredFieldId] = useStoredState<number | null>({
		defaultValue: null,
		key: "D45E1AE1437AE405",
	});

	return (
		<AsyncForm<ObjectAttributeField[], FormValues, void>
			fetch={() =>
				PartConfigurationAttributesApi.getSharedAttributeFieldsOfConfigurations({
					configurationIds: configurationIds,
				})
			}
			getDefaultValues={(data) => ({
				attributeFieldId: data.find((field) => field.id == storedFieldId)?.id ?? null,
			})}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.configurator.partConfigurationAttributeUpdate.form"),
				data: {
					partConfigurationIds: configurationIds,
				},
			}}
			render={({ data, control, watch }) => {
				const field = data.find((field) => field.id == watch("attributeFieldId"));
				return (
					<>
						<FormSelectField
							control={control}
							name={"attributeFieldId"}
							label={i18n.t("attribute")}
							defaultValue={storedFieldId}
							options={data}
							getOptionKey={(o) => o.id}
							getOptionLabel={(o) => o.label}
							rules={requireRule()}
						/>
						{field != null && (
							<ObjectAttributeFieldFormComponent
								control={control}
								name={"newValue"}
								fieldDefinition={field}
							/>
						)}
					</>
				);
			}}
			submit={async (values) => {
				if (values.attributeFieldId == null) return;
				setStoredFieldId(values.attributeFieldId);
				await PartConfigurationAttributesApi.updateAttributeValueOfConfigurations({
					configurationIds: configurationIds,
					attributeFieldId: values.attributeFieldId,
					newValue: values.newValue,
				});
			}}
		/>
	);
};
